<template>
  <!-- Карточка статьи. grid — плитка ленты, related — блок «Похожие статьи»:
       у него обложка внутри отступов и крупнее заголовок -->
  <NuxtLink
    :to="articleUrl"
    class="group flex flex-col rounded-2xl bg-white outline-none transition-[box-shadow,transform] duration-200 hover:shadow-[0_8px_28px_-12px_rgba(58,61,68,0.25)] focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2"
    :class="isRelated ? 'gap-4 p-4' : 'overflow-hidden'"
  >
    <div
      class="relative w-full shrink-0 overflow-hidden bg-[#EEF3FD]"
      :class="isRelated ? 'h-[160px] rounded-xl' : 'h-[190px] rounded-t-2xl'"
    >
      <NuxtImg
        v-if="article.cover"
        :src="article.cover"
        :alt="article.title"
        loading="lazy"
        class="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.04]"
      />
      <!-- Обложки может не быть — вместо пустого прямоугольника ставим знак блога -->
      <span v-else class="absolute inset-0 flex items-center justify-center text-[#2C6BFF]/25">
        <Icon name="lucide:newspaper" size="40" class="block text-[#2C6BFF]/25" />
      </span>
    </div>

    <div class="flex flex-1 flex-col" :class="isRelated ? '' : 'p-4'">
      <div class="flex flex-col gap-2">
        <span
          v-if="article.primaryTopic?.name"
          class="w-fit max-w-full truncate rounded-md bg-[#EEF3FD] px-1.5 py-0.5 text-[11px] font-semibold leading-[normal] text-[#2C6BFF]"
        >
          {{ article.primaryTopic.name }}
        </span>

        <p
          class="line-clamp-2 break-words font-medium text-txmain transition-colors group-hover:text-[#2C6BFF]"
          :class="isRelated ? 'text-lg leading-[1.3]' : 'text-base leading-[1.35]'"
        >
          {{ article.title }}
        </p>

        <p
          v-if="article.excerpt"
          class="line-clamp-3 break-words text-sm text-txmain/80"
          :class="isRelated ? 'leading-[1.4]' : 'leading-[1.45]'"
        >
          {{ article.excerpt }}
        </p>
      </div>

      <!-- В плитке мета — обычный элемент колонки с шагом 8, в «похожих» она
           отбита от текста на 12 (Meta pt-[12px] в макете) -->
      <div
        class="mt-auto flex items-center justify-between gap-3 text-xs leading-[normal] text-txopacity"
        :class="isRelated ? 'pt-3' : 'pt-2'"
      >
        <time v-if="article.publishedAt" :datetime="blogDateIso(article.publishedAt)">
          {{ blogDateShort(article.publishedAt) }}
        </time>
        <span v-else></span>

        <!-- Размер и цвет дублируем: не-scoped `div span` из app.vue задаёт
             вложенным элементам 14px/#3a3d44 в обход наследования -->
        <span
          v-if="article.views != null"
          class="flex shrink-0 items-center gap-1 text-xs font-normal leading-[normal] text-txopacity"
        >
          <Icon name="lucide:eye" size="12" class="block text-txopacity" />
          {{ blogViews(article.views) }}
        </span>
      </div>
    </div>
  </NuxtLink>
</template>

<script setup lang="ts">
  import type { BlogArticleCard } from "~/interfaces/blog";
  import { blogArticleUrl } from "~/common/blogUrl";
  import { blogDateIso, blogDateShort, blogViews } from "~/common/blogFormat";

  const props = withDefaults(
    defineProps<{ article: BlogArticleCard; variant?: "grid" | "related" }>(),
    { variant: "grid" }
  );

  const isRelated = computed(() => props.variant === "related");
  const articleUrl = computed(() => blogArticleUrl(props.article));
</script>

<style lang="scss" scoped>
  // Меню в лейауте объявляет не-scoped `a { width: 100% }` — карточка-ссылка
  // должна тянуться по сетке, а не по этому правилу
  a {
    width: auto;
  }
</style>
